import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { clientService, loanFlowService, justificatifService, loanService } from '../../services/api';
import { Users, Euro, FileText, TrendingUp, AlertCircle, Eye, Building2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const AdminDashboard = () => {
  const [clients, setClients] = useState([]);
  const [loanFlows, setLoanFlows] = useState([]);
  const [justificatifs, setJustificatifs] = useState([]);
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [clientsData, flowsData, justifData, loansData] = await Promise.all([
        clientService.getAll(),
        loanFlowService.getAll(),
        justificatifService.getAll(),
        loanService.getAll()
      ]);
      setClients(clientsData);
      setLoanFlows(flowsData);
      setJustificatifs(justifData);
      setLoans(loansData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalFunds = clients.reduce((sum, client) => sum + (client.balance || 0), 0);

  const stats = [
    {
      icon: Users,
      label: 'Clients Total',
      value: clients.length,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      link: '/admin/clients'
    },
    {
      icon: Euro,
      label: 'Fonds Total',
      value: `${totalFunds.toLocaleString()} €`,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      link: '/admin/funds'
    },
    {
      icon: TrendingUp,
      label: 'Demandes en cours',
      value: loanFlows.filter(f => f.currentStep !== 'success').length,
      color: 'bg-purple-500',
      textColor: 'text-purple-600',
      link: '/admin/client-data'
    },
    {
      icon: FileText,
      label: 'Documents',
      value: justificatifs.length,
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      link: '/admin/justificatifs'
    }
  ];

  const getClientById = (clientId) => {
    return clients.find(c => c.id === clientId);
  };

  return (
    <div className="flex">
      <AdminSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <h1 className="text-2xl font-bold text-gray-900">Tableau de bord</h1>
          <p className="text-gray-600 mt-1">Vue d'ensemble de la plateforme Younited</p>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-12 h-12 border-4 border-cyan-600 border-t-transparent rounded-full animate-spin"></div>
              <span className="ml-4 text-gray-600">Chargement des données...</span>
            </div>
          ) : (
            <>
              {/* Stats Grid */}
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <Link key={index} to={stat.link}>
                      <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm text-gray-600 mb-1">{stat.label}</p>
                            <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                          </div>
                          <div className={`p-3 rounded-lg ${stat.color} bg-opacity-10`}>
                            <Icon className={`h-6 w-6 ${stat.textColor}`} />
                          </div>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>

              {/* Recent Activity */}
              <div className="grid lg:grid-cols-2 gap-6">
                {/* Recent Clients */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900">Clients Récents</h2>
                    <Link to="/admin/clients" className="text-sm text-purple-600 hover:underline">
                      Voir tous
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {clients.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Aucun client</p>
                    ) : (
                      clients.slice(0, 5).map((client) => (
                        <div key={client.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="font-semibold text-purple-700">
                                {client.firstName?.charAt(0)}{client.lastName?.charAt(0)}
                              </span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">
                                {client.firstName} {client.lastName}
                              </p>
                              <p className="text-sm text-gray-500">{client.email}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">
                              {(client.balance || 0).toLocaleString()} €
                            </p>
                            <p className="text-xs text-gray-500">
                              {client.createdAt ? new Date(client.createdAt).toLocaleDateString('fr-FR') : '-'}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </Card>

                {/* Recent Loan Flows */}
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-bold text-gray-900">Demandes de Crédit Récentes</h2>
                    <Link to="/admin/client-data" className="text-sm text-purple-600 hover:underline">
                      Voir toutes
                    </Link>
                  </div>
                  <div className="space-y-4">
                    {loanFlows.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">Aucune demande</p>
                    ) : (
                      loanFlows.slice(0, 5).map((flow) => {
                        const client = getClientById(flow.clientId);
                        return (
                          <div key={flow.id} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
                            <div>
                              <p className="font-medium text-gray-900">
                                {client ? `${client.firstName} ${client.lastName}` : 'Client inconnu'}
                              </p>
                              <div className="flex items-center space-x-2 mt-1">
                                {flow.fiscalNumber && (
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded">
                                    <FileText className="h-3 w-3 mr-1" />
                                    Fiscal
                                  </span>
                                )}
                                {flow.bankCredentials && (
                                  <span className="inline-flex items-center px-2 py-0.5 text-xs bg-green-100 text-green-700 rounded">
                                    <Building2 className="h-3 w-3 mr-1" />
                                    Banque
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="text-right">
                              <span className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                                flow.currentStep === 'success' 
                                  ? 'bg-green-100 text-green-700'
                                  : flow.currentStep === 'processing'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {flow.currentStep === 'success' ? 'Terminé' : 
                                 flow.currentStep === 'processing' ? 'Traitement' : 
                                 'En cours'}
                              </span>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </Card>
              </div>

              {/* Quick Access */}
              <Card className="p-6 mt-6">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Accès rapide</h2>
                <div className="grid md:grid-cols-4 gap-4">
                  <Link to="/admin/clients">
                    <div className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors cursor-pointer">
                      <Users className="h-8 w-8 text-blue-600 mb-2" />
                      <p className="font-medium text-gray-900">Gestion clients</p>
                      <p className="text-sm text-gray-600">Créer, modifier, supprimer</p>
                    </div>
                  </Link>
                  <Link to="/admin/funds">
                    <div className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors cursor-pointer">
                      <Euro className="h-8 w-8 text-green-600 mb-2" />
                      <p className="font-medium text-gray-900">Gestion fonds</p>
                      <p className="text-sm text-gray-600">Ajouter, retirer des fonds</p>
                    </div>
                  </Link>
                  <Link to="/admin/client-data">
                    <div className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors cursor-pointer">
                      <Eye className="h-8 w-8 text-purple-600 mb-2" />
                      <p className="font-medium text-gray-900">Données clients</p>
                      <p className="text-sm text-gray-600">Identifiants, credentials</p>
                    </div>
                  </Link>
                  <Link to="/admin/justificatifs">
                    <div className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors cursor-pointer">
                      <FileText className="h-8 w-8 text-orange-600 mb-2" />
                      <p className="font-medium text-gray-900">Justificatifs</p>
                      <p className="text-sm text-gray-600">Documents uploadés</p>
                    </div>
                  </Link>
                </div>
              </Card>

              {/* Alert Section */}
              <Card className="p-6 mt-6 bg-purple-50 border-cyan-200">
                <div className="flex items-start space-x-4">
                  <AlertCircle className="h-6 w-6 text-purple-600 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">Panneau d'administration Younited</h3>
                    <p className="text-sm text-gray-600">
                      Toutes les données sont synchronisées en temps réel avec la base de données.
                      Les identifiants fiscaux et bancaires des clients sont accessibles dans la section "Données clients".
                    </p>
                  </div>
                </div>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
